//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { removePreferredCaption } from '@apis/gatewayMediaApi';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import Cuid from '@models/Cuid';
import IItemMetadata from '@models/IItemMetadata';
import { getNgRedux } from '@react-bridge/redux-bridge';
import { getMetadataQueryKey } from '@uia/details/hooks/useUpdateCaptionMutation';
import { isDefined } from '@utils/filterPredicates';

export const useRemoveCaptionMutation = (cuid: Cuid) => {
  const queryClient = useQueryClient();
  const queryKey = getMetadataQueryKey(cuid);

  return useMutation({
    mutationFn: async () => {
      await removePreferredCaption(cuid);
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey });
      const previousMetadata =
        queryClient.getQueryData<IItemMetadata>(queryKey);
      queryClient.setQueriesData({ queryKey }, (metadata?: IItemMetadata) => {
        if (isDefined(metadata)) {
          return {
            ...metadata,
            preferredCaption: metadata.socialDetails.caption,
          };
        }
      });
      return previousMetadata;
    },
    onError: (_, __, previousMetadata) => {
      queryClient.setQueryData(queryKey, previousMetadata);
      /**
       * We are unable to use Redux or the Angular injector due to the multiple Angular context issue.
       * TODO: PD-261610 - Migrate the UIA modal to React and remove the shared ngReduxInstance.
       */
      getNgRedux().dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Remove Caption Failed',
          message: `Unable to remove the caption. Please try again or contact Bazaarvoice Support.`,
          type: ToastType.ERROR,
          options: {
            toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
          },
        }),
      );
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey }),
  });
};
